import { extendTheme } from '@chakra-ui/react';

// Custom color palette
const colors = {
  brand: {
    50: '#e6f9f0',
    100: '#b8eed6',
    200: '#8ae3bc',
    300: '#5cd8a2',
    400: '#2ecd88',
    500: '#14b36e', // Primary green accent
    600: '#0e8a54',
    700: '#09613a',
    800: '#033820',
    900: '#001006',
  },
  gray: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#374151',
    800: '#1F2937',
    900: '#111827',
  },
};

// Border radius system
const radii = {
  none: '0',
  sm: '0.125rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '1rem', // Default for cards, sidebar, header, etc.
  full: '9999px', // For pill-style buttons/inputs
};

// Typography
const fonts = {
  heading: 'Inter, system-ui, sans-serif',
  body: 'Inter, system-ui, sans-serif',
};
const fontWeights = {
  normal: 400,
  medium: 500,
  bold: 700,
};
const fontSizes = {
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem',
  '5xl': '3rem',
};

// Component overrides for flat, minimal, pill-style look
const components = {
  Button: {
    baseStyle: {
      borderRadius: 'full',
      boxShadow: 'none',
      fontWeight: 'medium',
    },
    defaultProps: {
      colorScheme: 'brand',
      variant: 'solid',
    },
  },
  Input: {
    baseStyle: {
      field: {
        borderRadius: 'full',
        boxShadow: 'none',
      },
    },
    variants: {
      filled: {
        field: {
          bg: 'gray.50',
          _hover: { bg: 'gray.100' },
          _focus: { bg: 'white', borderColor: 'brand.500' },
        },
      },
    },
  },
  Select: {
    baseStyle: {
      field: {
        borderRadius: 'full',
        boxShadow: 'none',
      },
    },
    variants: {
      filled: {
        field: {
          bg: 'gray.50',
          _hover: { bg: 'gray.100' },
          _focus: { bg: 'white', borderColor: 'brand.500' },
        },
      },
    },
  },
  Box: {
    baseStyle: {
      borderRadius: 'xl',
      boxShadow: 'none',
    },
  },
  Card: {
    baseStyle: {
      borderRadius: 'xl',
      boxShadow: 'none',
    },
  },
  Table: {
    variants: {
      simple: {
        th: {
          bg: 'gray.50',
          color: 'gray.700',
          fontWeight: 'medium',
        },
        td: {
          borderColor: 'gray.200',
        },
      },
      striped: {
        th: {
          bg: 'gray.50',
          color: 'gray.700',
          fontWeight: 'medium',
        },
        td: {
          borderColor: 'gray.200',
        },
      },
    },
    defaultProps: {
      variant: 'simple',
      size: 'sm',
    },
  },
};

// Breakpoints for responsive design
const breakpoints = {
  base: '0em', // 0px
  sm: '30em', // 480px
  md: '48em', // 768px
  lg: '62em', // 992px
  xl: '80em', // 1280px
  '2xl': '96em', // 1536px
};

// Subtle border color for light/dark mode
const borders = {
  subtle: {
    light: '1px solid #E5E7EB',
    dark: '1px solid #374151',
  },
};

// Export the custom theme
const theme = extendTheme({
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },
  colors,
  radii,
  fonts,
  fontWeights,
  fontSizes,
  components,
  breakpoints,
  borders,
  styles: {
    global: {
      body: {
        overflowX: 'hidden',
      },
      '*:focus-visible': {
        outline: '2px solid',
        outlineColor: 'brand.500',
        outlineOffset: '2px',
      },
    },
  },
});

export default theme; 